import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { adminApi } from '@/services/adminApi'
import type { Provider } from '@/types/admin'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'

interface ProductModelFallback {
  id: string
  llmModelId: string
  priority: number
}

interface ProductModel {
  id: string
  name: string
  description: string | null
  modelName: string
  providerId: string
  provider: { id: string; name: string }
  displayOrder: number
  isActive: boolean
  isDefault: boolean
  tier: number
  bestAt: string | null
  fallbacks?: ProductModelFallback[]
}

interface LLMModel {
  id: string
  name: string
  displayName: string | null
  providerId: string
  provider: { id: string; name: string }
}

interface FormState {
  name: string
  description: string
  modelName: string
  providerId: string
  displayOrder: number
  isActive: boolean
  isDefault: boolean
  tier: number
  bestAt: string
  fallbackLlmModelIds: string[]
}

const emptyForm: FormState = {
  name: '',
  description: '',
  modelName: '',
  providerId: '',
  displayOrder: 0,
  isActive: true,
  isDefault: false,
  tier: 2,
  bestAt: '',
  fallbackLlmModelIds: [],
}

export default function ProductModelsPage() {
  const [productModels, setProductModels] = useState<ProductModel[]>([])
  const [providers, setProviders] = useState<Provider[]>([])
  const [allLLMModels, setAllLLMModels] = useState<LLMModel[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingModel, setEditingModel] = useState<ProductModel | null>(null)
  const [formState, setFormState] = useState<FormState>(emptyForm)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [pm, prov, llmModels] = await Promise.all([
        adminApi.getProductModels(),
        adminApi.getProviders(),
        adminApi.getAllLLMModels(),
      ])
      setProductModels(pm)
      setProviders(prov)
      setAllLLMModels(llmModels)
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load data')
    } finally {
      setLoading(false)
    }
  }

  const modelsForProvider = useMemo(() => {
    if (!formState.providerId) return []
    return allLLMModels.filter(m => m.providerId === formState.providerId)
  }, [allLLMModels, formState.providerId])

  const providerOptions = useMemo(
    () => providers.map(p => ({ value: p.id, label: p.name })),
    [providers]
  )

  const modelOptions = useMemo(
    () => modelsForProvider.map(m => ({ value: m.name, label: m.displayName || m.name })),
    [modelsForProvider]
  )

  const llmModelById = useMemo(() => {
    const map = new Map<string, LLMModel>()
    for (const m of allLLMModels) map.set(m.id, m)
    return map
  }, [allLLMModels])

  const availableFallbackOptions = useMemo(() => {
    const selected = new Set(formState.fallbackLlmModelIds)
    return allLLMModels
      .filter(m => !selected.has(m.id))
      .map(m => ({
        value: m.id,
        label: `${m.provider.name} / ${m.displayName || m.name}`,
      }))
  }, [allLLMModels, formState.fallbackLlmModelIds])

  const addFallback = (llmModelId: string) => {
    if (!llmModelId) return
    setFormState(prev =>
      prev.fallbackLlmModelIds.includes(llmModelId)
        ? prev
        : { ...prev, fallbackLlmModelIds: [...prev.fallbackLlmModelIds, llmModelId] }
    )
  }

  const removeFallback = (llmModelId: string) => {
    setFormState(prev => ({
      ...prev,
      fallbackLlmModelIds: prev.fallbackLlmModelIds.filter(id => id !== llmModelId),
    }))
  }

  const moveFallback = (llmModelId: string, direction: -1 | 1) => {
    setFormState(prev => {
      const ids = [...prev.fallbackLlmModelIds]
      const index = ids.indexOf(llmModelId)
      const target = index + direction
      if (index < 0 || target < 0 || target >= ids.length) return prev
      ;[ids[index], ids[target]] = [ids[target], ids[index]]
      return { ...prev, fallbackLlmModelIds: ids }
    })
  }

  const openCreate = () => {
    setEditingModel(null)
    setFormState({ ...emptyForm, displayOrder: productModels.length })
    setIsFormOpen(true)
  }

  const openEdit = (pm: ProductModel) => {
    setEditingModel(pm)
    const orderedFallbackIds = (pm.fallbacks ?? [])
      .slice()
      .sort((a, b) => a.priority - b.priority)
      .map(f => f.llmModelId)
    setFormState({
      name: pm.name,
      description: pm.description || '',
      modelName: pm.modelName,
      providerId: pm.providerId,
      displayOrder: pm.displayOrder,
      isActive: pm.isActive,
      isDefault: pm.isDefault,
      tier: pm.tier ?? 2,
      bestAt: pm.bestAt || '',
      fallbackLlmModelIds: orderedFallbackIds,
    })
    setIsFormOpen(true)
  }

  const closeForm = () => {
    setIsFormOpen(false)
    setEditingModel(null)
    setFormState(emptyForm)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!formState.name.trim() || !formState.modelName || !formState.providerId) {
      toast.error('Name, provider, and model are required')
      return
    }

    try {
      setSaving(true)
      const payload = {
        name: formState.name.trim(),
        description: formState.description.trim() || undefined,
        modelName: formState.modelName,
        providerId: formState.providerId,
        displayOrder: formState.displayOrder,
        isActive: formState.isActive,
        isDefault: formState.isDefault,
        tier: formState.tier,
        bestAt: formState.bestAt.trim() || undefined,
        fallbackLlmModelIds: formState.fallbackLlmModelIds,
      }

      if (editingModel) {
        await adminApi.updateProductModel(editingModel.id, payload)
        toast.success('Product model updated')
      } else {
        await adminApi.createProductModel(payload)
        toast.success('Product model created')
      }
      closeForm()
      await loadData()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save product model')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (pm: ProductModel) => {
    if (!confirm(`Delete product model "${pm.name}"?`)) return
    try {
      await adminApi.deleteProductModel(pm.id)
      toast.success('Product model deleted')
      await loadData()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to delete product model')
    }
  }

  const toggleActive = async (pm: ProductModel) => {
    try {
      await adminApi.updateProductModel(pm.id, { isActive: !pm.isActive })
      toast.success(`Product model ${pm.isActive ? 'deactivated' : 'activated'}`)
      await loadData()
    } catch (error: any) {
      toast.error(error?.message || 'Failed to update product model')
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-gray-900">Product Models</h1>
          <p className="mt-1 text-sm text-gray-600">
            Branded model tiers shown to end users (e.g. "Libra 0.9", "Libra 1.0 Max")
          </p>
        </div>
        <Button onClick={openCreate} className="bg-blue-600 hover:bg-blue-700">
          New Product Model
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-left">
            Product Models ({productModels.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="py-10 text-center text-gray-600">Loading...</div>
          ) : productModels.length === 0 ? (
            <div className="py-10 text-center text-gray-500">
              No product models configured. Users will see raw LLM model names.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Order</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Description</TableHead>
                  <TableHead>Underlying Model</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Tier</TableHead>
                  <TableHead>Fallbacks</TableHead>
                  <TableHead>Best At</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Default</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {productModels.map(pm => (
                  <TableRow key={pm.id}>
                    <TableCell>{pm.displayOrder}</TableCell>
                    <TableCell className="font-medium text-gray-900">{pm.name}</TableCell>
                    <TableCell className="text-gray-600">{pm.description || '—'}</TableCell>
                    <TableCell className="font-mono text-sm">{pm.modelName}</TableCell>
                    <TableCell>{pm.provider?.name || '—'}</TableCell>
                    <TableCell>
                      <span className="inline-flex items-center rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                        {pm.tier}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
                          (pm.fallbacks?.length ?? 0) > 0
                            ? 'bg-emerald-100 text-emerald-800'
                            : 'bg-amber-100 text-amber-800'
                        }`}
                        title={
                          (pm.fallbacks?.length ?? 0) > 0
                            ? `${pm.fallbacks!.length} fallback${pm.fallbacks!.length === 1 ? '' : 's'} configured`
                            : 'No fallbacks — primary failures fall through to org chat model only'
                        }
                      >
                        {pm.fallbacks?.length ?? 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-gray-600 text-xs max-w-[200px] truncate" title={pm.bestAt || ''}>
                      {pm.bestAt || '—'}
                    </TableCell>
                    <TableCell>
                      <button
                        onClick={() => toggleActive(pm)}
                        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium cursor-pointer ${
                          pm.isActive
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {pm.isActive ? 'Active' : 'Inactive'}
                      </button>
                    </TableCell>
                    <TableCell>
                      {pm.isDefault && (
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                          Default
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={() => openEdit(pm)}>
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:text-red-700"
                          onClick={() => handleDelete(pm)}
                        >
                          Delete
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-lg">
            <h2 className="text-xl font-semibold text-gray-900">
              {editingModel ? 'Edit Product Model' : 'Create Product Model'}
            </h2>
            <p className="mt-1 text-sm text-gray-600">
              {editingModel
                ? 'Update the branded model configuration.'
                : 'Create a new branded model tier for end users.'}
            </p>
            <form onSubmit={handleSubmit} className="mt-6 space-y-4">
              <Input
                label="Name"
                value={formState.name}
                onChange={e => setFormState(prev => ({ ...prev, name: e.target.value }))}
                placeholder='e.g. "Libra 0.9"'
                required
              />
              <Input
                label="Description"
                value={formState.description}
                onChange={e => setFormState(prev => ({ ...prev, description: e.target.value }))}
                placeholder='e.g. "Fast, everyday model"'
              />
              <Select
                label="Provider"
                value={formState.providerId}
                onChange={e => {
                  setFormState(prev => ({ ...prev, providerId: e.target.value, modelName: '' }))
                }}
                options={[{ value: '', label: 'Select a provider...' }, ...providerOptions]}
                required
              />
              <Select
                label="Model"
                value={formState.modelName}
                onChange={e => setFormState(prev => ({ ...prev, modelName: e.target.value }))}
                options={[{ value: '', label: 'Select a model...' }, ...modelOptions]}
                disabled={!formState.providerId || modelOptions.length === 0}
                required
              />
              {formState.providerId && modelOptions.length === 0 && (
                <p className="text-sm text-amber-600">
                  No models available for this provider.
                </p>
              )}
              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Display Order"
                  type="number"
                  value={String(formState.displayOrder)}
                  onChange={e => setFormState(prev => ({ ...prev, displayOrder: parseInt(e.target.value) || 0 }))}
                />
                <Select
                  label="Tier"
                  value={String(formState.tier)}
                  onChange={e => setFormState(prev => ({ ...prev, tier: parseInt(e.target.value) || 2 }))}
                  options={[
                    { value: '1', label: '1 — Lightweight' },
                    { value: '2', label: '2 — Standard' },
                    { value: '3', label: '3 — Pro' },
                    { value: '4', label: '4 — Max' },
                  ]}
                />
              </div>
              <Input
                label="Best At (for model promotion)"
                value={formState.bestAt}
                onChange={e => setFormState(prev => ({ ...prev, bestAt: e.target.value }))}
                placeholder='e.g. "Coding, writing, structured extraction"'
              />
              <div className="space-y-2 rounded-md border border-gray-200 bg-gray-50 p-3">
                <div>
                  <label className="text-sm font-medium text-gray-900">Routing fallback chain</label>
                  <p className="text-xs text-gray-600">
                    When this product model is unhealthy or slow, the routing layer tries these in order. Drag to reorder. First entry = first fallback tried.
                  </p>
                </div>
                {formState.fallbackLlmModelIds.length === 0 ? (
                  <p className="text-xs text-gray-500 italic">No fallbacks configured. Routing will use the org chat-model last-resort only.</p>
                ) : (
                  <ol className="space-y-1">
                    {formState.fallbackLlmModelIds.map((llmModelId, index) => {
                      const llm = llmModelById.get(llmModelId)
                      return (
                        <li
                          key={llmModelId}
                          className="flex items-center justify-between rounded border border-gray-200 bg-white px-3 py-2 text-sm"
                        >
                          <div className="flex items-center gap-2">
                            <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-gray-200 text-xs font-medium text-gray-700">
                              {index + 1}
                            </span>
                            <span className="font-medium text-gray-900">
                              {llm ? `${llm.provider.name} / ${llm.displayName || llm.name}` : `(missing model: ${llmModelId})`}
                            </span>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => moveFallback(llmModelId, -1)}
                              disabled={index === 0}
                              className="rounded px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                              aria-label="Move up"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              onClick={() => moveFallback(llmModelId, 1)}
                              disabled={index === formState.fallbackLlmModelIds.length - 1}
                              className="rounded px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed"
                              aria-label="Move down"
                            >
                              ↓
                            </button>
                            <button
                              type="button"
                              onClick={() => removeFallback(llmModelId)}
                              className="rounded px-2 py-0.5 text-xs text-red-600 hover:bg-red-50"
                              aria-label="Remove"
                            >
                              ✕
                            </button>
                          </div>
                        </li>
                      )
                    })}
                  </ol>
                )}
                <Select
                  label=""
                  value=""
                  onChange={e => {
                    addFallback(e.target.value)
                    e.target.value = ''
                  }}
                  options={[
                    { value: '', label: '+ Add fallback model...' },
                    ...availableFallbackOptions,
                  ]}
                  disabled={availableFallbackOptions.length === 0}
                />
              </div>
              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formState.isActive}
                    onChange={e => setFormState(prev => ({ ...prev, isActive: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  Active
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={formState.isDefault}
                    onChange={e => setFormState(prev => ({ ...prev, isDefault: e.target.checked }))}
                    className="rounded border-gray-300"
                  />
                  Default (used for "Auto" mode)
                </label>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={closeForm} disabled={saving}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={saving || !formState.name || !formState.modelName || !formState.providerId}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  {saving ? 'Saving...' : editingModel ? 'Save Changes' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
